import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AlertUI from 'app/components/layout/Alerts/AlertUI';
import { useBoolean } from 'app/components/v2/hooks';
import { pluralize } from 'app/utils/lib/string';

// Local Typings
interface Props {
  onKeepSessionAlive: () => void;
  onLogout: () => void;
}

const EndOfSessionWarning: FC<Props> = ({
  onKeepSessionAlive,
  onLogout,
}) => {
  const { t } = useTranslation('components');
  const [seconds, setSeconds] = useState(30);
  const { handleSetTrue: handleExpire, isTrue: isExpired } = useBoolean(false);

  const secondsRemaining = pluralize(Math.max(seconds,
    0),
  'second');

  useEffect(() => {
    if (seconds <= 0 && !isExpired) {
      handleExpire();
    }
  },
  [handleExpire, isExpired, seconds]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(seconds - 1);
    },
    1000);

    return () => {
      clearInterval(interval);
    };
  },
  [seconds]);

  useEffect(() => {
    if (isExpired) {
      onLogout();
    }
  },
  [isExpired, onLogout]);

  return (
    <AlertUI
      actions={[
        {
          isPrimaryAction: true,
          label: t('sessionAlert.stayLoggedIn', 'Stay logged in'),
          onClick: onKeepSessionAlive,
        },
      ]}
      message={t('sessionAlert.alert', 'Your session will expire in {{secondsRemaining}}', { secondsRemaining })}
      title={t('sessionAlert.title', 'Logging out...')}
    />
  );
};

export default EndOfSessionWarning;
